import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { type AgentEnvironmentStatus } from "common";

/**
 * ⚠️ `credential` is the plaintext. Attach and reissue are the only two calls that ever return
 * it, and each returns it exactly once, matching `credentialResponse` in
 * backend/src/routes/agent.ts. It belongs in the card's component state only, for exactly as
 * long as the card can still show it, and must never reach the query cache, `localStorage`, a
 * URL, or a log. `fingerprint` is derived from the hash, so it is safe to keep and display
 * forever, which is what the card falls back to once the plaintext is gone.
 */
export type IssuedAgent = {
  agentId: number;
  credential: string;
  fingerprint: string;
  installCommand: string;
  /**
   * The origin for the `configure --server <SERVER>` step. Sent by the server rather than
   * parsed out of `installCommand`, which is a whole shell string.
   */
  serverUrl: string;
};

/**
 * Matches the status route's response, whose shape lives in common so the controller and this client
 * cannot drift (agent-4-2). Polled continuously rather than read once, unlike IssuedAgent above, so it
 * carries no secret: `fingerprint` is the hash-derived one, safe to show forever. `capabilities` is
 * what the connected agent advertised, and null while none is connected.
 */
export type AgentStatus = AgentEnvironmentStatus;

/**
 * Exactly what the agent's `probe` answers (agent/foundry_agent/control.py), passed through the
 * dispatch untouched: `probeResponse` in backend/src/routes/agent.ts marks `result` as
 * `.unknown(true)` on purpose, so this shape belongs to the agent, not to a schema on this side.
 * Carries no secret, but `path` and `locale` do carry raw environment values, so treat the whole
 * object the way `IssuedAgent`'s comment above already asks: fine to render, never to log
 * wholesale.
 */
export type ProbeResult = {
  agentVersion: string;
  pythonVersion: string;
  hostname: string;
  shell: string;
  bashAvailable: boolean;
  path: string;
  moduleAvailable: boolean;
  umask: string;
  locale: { lang: string; lcAll: string; preferred: string };
  ulimits: Record<string, [number, number]>;
};

// Shared by the query itself and by the two mutations that have to invalidate it, so a rename
// here cannot leave one of the three still keyed on the old value.
const agentStatusQueryKey = (
  environmentId?: number
): (string | number | undefined)[] => ["agent-status", environmentId];

/**
 * ⚠️ NOT /api/user/v1, unlike every sibling in this directory (profile.api.tsx,
 * project-sharing.api.tsx). The agent router mounts directly off the API root, dual mounted at
 * both /agent/v1 and /v1/agent (backend/src/routes/_base.ts:93-94), not under /user/v1 at all.
 * /v1/agent is used here because it is the fullPath every route in backend/src/routes/agent.ts
 * declares itself under; that file, not this comment, is the source of truth if the two ever
 * disagree. One constant so the three calls below cannot drift from each other or from it.
 */
const AGENT_ENVIRONMENT_BASE = "/api/v1/agent/environment";

/**
 * Polls while this card is mounted, which is only while the environment is saved and the Agent
 * transport is chosen. `enabled` is what keeps a brand new, unsaved environment from firing a
 * request with `undefined` in the path.
 */
export const useAgentStatus = (
  environmentId?: number
): UseQueryResult<AgentStatus, Error> =>
  useQuery<AgentStatus, Error>({
    queryKey: agentStatusQueryKey(environmentId),
    queryFn: async () => {
      const res = await axios.get(
        `${AGENT_ENVIRONMENT_BASE}/${environmentId}/status`
      );
      return res.data as AgentStatus;
    },
    enabled: Boolean(environmentId),
    refetchInterval: 5000,
  });

export const useAttachAgent = (
  environmentId?: number
): UseMutationResult<IssuedAgent, unknown, void> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${AGENT_ENVIRONMENT_BASE}/${environmentId}/attach`
      );
      return res.data as IssuedAgent;
    },
    onSuccess: (): void => {
      // Only the status query is refreshed here. The credential itself goes straight from the
      // response into the card's own state, set by the caller's onSuccess, never through here.
      queryClient.invalidateQueries({
        queryKey: agentStatusQueryKey(environmentId),
      });
    },
    onError: (error: unknown): void => {
      // The backend answers 409 when this environment already has an agent, which is the one
      // failure a scientist can actually act on: Reissue is the fix, not trying Attach again.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      setNotification({
        id: "agent-attach-failed",
        message: "Could not attach your agent",
        details:
          status === 409
            ? "This run environment already has an agent attached. Use Reissue below if its credential was lost, rather than attaching a second one."
            : "The request to attach an agent did not go through. Check your connection and try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * The only recovery from a lost credential: rotation needs a connected agent, and an agent
 * whose secret is lost can never connect to ask for one.
 */
export const useReissueAgentCredential = (
  environmentId?: number
): UseMutationResult<IssuedAgent, unknown, void> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${AGENT_ENVIRONMENT_BASE}/${environmentId}/reissue`
      );
      return res.data as IssuedAgent;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: agentStatusQueryKey(environmentId),
      });
    },
    onError: (): void => {
      setNotification({
        id: "agent-reissue-failed",
        message: "Could not reissue the credential",
        details:
          "The request to replace the agent credential did not go through. Check your connection and try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Test Connection. Spawns nothing on the login node; it is the one call an operator makes to see
 * what that machine looks like from the agent's own point of view right now. A 409 here is a
 * STATED REASON, not a generic failure: the hub can be off, or the agent attached but without a
 * live socket, and the card, not this hook, is what turns that into words a scientist can act on
 * (agent-card.tsx). No cache invalidation on success: unlike attach and reissue, a probe changes
 * nothing about this environment's status.
 */
export const useProbeAgent = (
  environmentId?: number
): UseMutationResult<
  { result: ProbeResult; durationMs: number },
  unknown,
  void
> =>
  useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${AGENT_ENVIRONMENT_BASE}/${environmentId}/probe`
      );
      return res.data as { result: ProbeResult; durationMs: number };
    },
  });
